/**
 * Voice and pitch rules (PLAN §2.4) as pure functions. The pointer layer and play screen apply them;
 * nothing here touches Web Audio.
 */
import { glideSeconds, type Scale } from '@tonist/shared';
import { anchorDegree, degreeSemitone } from './tuning';

export { glideSeconds };

export const VOICE_CAP = 5;
export const IDLE_EVOLVE_MS = 20_000;
export const RETUNE_GLIDE_SEC = 2;
export const RETUNE_STAGGER_SEC = 0.12;
export const AUTO_OCTAVE_DB = -18;
export const STEAL_FADE_SEC = 0.3;

const DISSONANT_CLASSES = new Set([1, 6, 11]);

const intervalClassSt = (a: number, b: number): number => (((Math.round(b - a) % 12) + 12) % 12);

/** Is `st` (absolute semitones from Sa3) dissonant (b2 / tritone) against any sounding semitone? */
export function isDissonantAgainst(st: number, soundingSt: readonly number[]): boolean {
  return soundingSt.some((s) => DISSONANT_CLASSES.has(intervalClassSt(s, st)));
}

export interface GuardResult { degree: number; nudged: boolean }

/**
 * Consonance guard. `sounding` are the absolute semitone offsets (from Sa3) of the voices currently sounding.
 * Applies only to the third voice and up (two sounding voices already). Never nudges a defining degree.
 * Nudges one degree to the nearest consonant step (down first, then up).
 */
export function consonanceGuard(scale: Scale, degree: number, octave: number, sounding: readonly number[]): GuardResult {
  if (sounding.length < 2) return { degree, nudged: false };
  const n = scale.semitones.length;
  const wrapped = ((degree % n) + n) % n;
  if (scale.defining.includes(wrapped)) return { degree, nudged: false };
  const stOf = (d: number) => degreeSemitone(scale, d) + 12 * octave;
  if (!isDissonantAgainst(stOf(degree), sounding)) return { degree, nudged: false };
  for (const delta of [-1, 1, -2, 2]) {
    const cand = degree + delta;
    if (!isDissonantAgainst(stOf(cand), sounding)) return { degree: cand, nudged: true };
  }
  return { degree, nudged: false };
}

export interface RetuneItem<T = unknown> { id: T; hz: number }
export interface RetuneStep<T = unknown> { id: T; delaySec: number; glideSec: number }

/** Tonic change with latched drones: lowest first, staggered 120 ms, 2 s glide each. */
export function retunePlan<T>(latched: readonly RetuneItem<T>[], stagger = RETUNE_STAGGER_SEC, glide = RETUNE_GLIDE_SEC): RetuneStep<T>[] {
  return [...latched].sort((a, b) => a.hz - b.hz).map((v, i) => ({ id: v.id, delaySec: i * stagger, glideSec: glide }));
}

export interface CapVoice<T = unknown> { id: T; latched: boolean; startTime: number; exempt?: boolean }

/**
 * Voice cap: returns the voice to steal (oldest non-latched, non-exempt) if adding one more would exceed the cap,
 * else null. Exempt voices (the bindu root drone) never count and are never stolen.
 */
export function voiceToSteal<T>(voices: readonly CapVoice<T>[], cap = VOICE_CAP): T | null {
  const counted = voices.filter((v) => !v.exempt);
  if (counted.length < cap) return null;
  const candidates = counted.filter((v) => !v.latched).sort((a, b) => a.startTime - b.startTime);
  if (candidates.length > 0) return candidates[0]!.id;
  return [...counted].sort((a, b) => a.startTime - b.startTime)[0]!.id;
}

/** Auto-octave: latched drones get a −18 dB octave-below partner, except the anchor degree (keeps Pa–Sa clean). */
export function autoOctaveFor(scale: Scale, degree: number, latched: boolean): boolean {
  if (!latched) return false;
  const n = scale.semitones.length;
  const wrapped = ((degree % n) + n) % n;
  return wrapped !== anchorDegree(scale);
}

/** Idle evolution triggers after 20 s without touches. */
export const idleEvolutionDue = (lastTouchMs: number, nowMs: number, idleMs = IDLE_EVOLVE_MS): boolean => nowMs - lastTouchMs >= idleMs;

export interface IdleWalkState { bendCents: number; brightness: number }

/** One step of the idle random walk: bend stays within ±12 cents, brightness within 0..1. */
export function stepIdleWalk(prev: IdleWalkState, rand: () => number = Math.random, dtSec = 1 / 60): IdleWalkState {
  const k = Math.min(1, dtSec * 6);
  const bend = prev.bendCents + (rand() - 0.5) * 8 * k;
  const bright = prev.brightness + (rand() - 0.5) * 0.3 * k;
  return { bendCents: Math.max(-12, Math.min(12, bend)), brightness: Math.max(0, Math.min(1, bright)) };
}

/** Options for a Play-mode noteOn given the rules. */
export function noteOnOptions(velocity: number): { attackScale: number } {
  // a fast touch shortens the swell a little; a still one keeps the drone's full attack
  return { attackScale: 1 - 0.5 * Math.max(0, Math.min(1, velocity)) };
}
